import React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import EsgMonthlyChart from "./EsgMonthlyChart";
import { formatNumber } from "../../lib/formatNumber";

const ELECTRICITY_COLOR = "hsl(var(--primary))";
const GHG_COLOR = "hsl(var(--secondary-accent))";
const WATER_COLOR = "hsl(var(--secondary-accent))";

const NotTrackedYet = ({ label }) => (
  <div className="rounded-lg border border-dashed border-border/20 p-8 text-center">
    <p className="text-sm text-foreground font-medium">{label} data is not tracked yet.</p>
    <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
      Covered by the identified gaps in the current ESG roadmap - see the Group Sustainability Report for details.
    </p>
  </div>
);

// Company-wide point-in-time figures (Siemens Energy ESG questionnaire) -
// not per-month/per-site like Environment, so a flat metric/value table
// rather than a chart.
const MetricTable = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-border/5">
            <td className="px-3 py-2.5 text-foreground">{r.metric}</td>
            <td className="px-3 py-2.5 text-right text-foreground font-medium whitespace-nowrap">
              {typeof r.value === "number" || !Number.isNaN(Number(r.value)) ? formatNumber(Number(r.value)) : r.value}
              {r.unit ? ` ${r.unit}` : ""}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="text-xs text-muted-foreground mt-4">
      Company-wide, from QP's Siemens Energy ESG Assessment - not broken down by site.
    </p>
  </div>
);

const IntegrationPending = () => (
  <div className="rounded-lg border border-dashed border-border/20 p-8 text-center">
    <p className="text-sm text-foreground font-medium">Industry 4.0 Integration pending.</p>
    <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
      Power Factor, THDI and THDV are captured by site-level power quality meters, not yet connected to this
      dashboard - real-time figures will appear here once that integration is live.
    </p>
  </div>
);

const MONTHLY_TABLE_COLUMNS = [
  { key: "electricity_kvah", label: "Electricity (kVAh)" },
  { key: "solar_kwh", label: "Solar (kWh)" },
  { key: "ghg_total_tco2e", label: "GHG (tCO2e)" },
  { key: "water_withdrawal_kl", label: "Water (kL)" },
  { key: "waste_kg", label: "Waste (kg)" },
  { key: "diesel_litres", label: "Diesel (L)" },
  { key: "co2_gas_kg", label: "CO2 Gas (kg)" },
  { key: "hvac_gas_kg", label: "HVAC Gas (kg)" },
  { key: "lpg_kg", label: "LPG (kg)" },
  { key: "compressed_air_nm3", label: "Compressed Air (Nm3)" },
  { key: "paints_kg", label: "Paints (kg)" },
];

const MonthlyTable = ({ monthly }) => (
  <div className="mt-6 overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-muted-foreground uppercase text-xs tracking-wide border-b border-border/10">
          <th className="px-3 py-2">Month</th>
          {MONTHLY_TABLE_COLUMNS.map((c) => (
            <th key={c.key} className="px-3 py-2 whitespace-nowrap">
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {monthly.map((row) => (
          <tr key={row.month} className="border-b border-border/5">
            <td className="px-3 py-2 text-foreground font-medium whitespace-nowrap">{row.month}</td>
            {MONTHLY_TABLE_COLUMNS.map((c) => (
              <td key={c.key} className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                {formatNumber(row[c.key])}
              </td>
            ))}
          </tr>
        ))}
        <tr className="border-t border-border/20">
          <td className="px-3 py-2.5 text-foreground font-semibold whitespace-nowrap">Total</td>
          {MONTHLY_TABLE_COLUMNS.map((c) => (
            <td key={c.key} className="px-3 py-2.5 text-foreground font-semibold whitespace-nowrap">
              {formatNumber(monthly.reduce((sum, row) => sum + (Number(row[c.key]) || 0), 0))}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  </div>
);

// Shared ESG (Environment / Social / Governance / Power Quality) tabbed
// section, used on both the group-wide dashboard and each individual
// site's page. Environment is per-site monthly data; Social/Governance are
// company-wide point-in-time figures (only passed in on the group
// dashboard - SiteDetail doesn't pass them, so per-site pages fall back to
// "not tracked yet" since these numbers aren't attributable to one site).
// Power Quality shows that it's waiting on an Industry 4.0 meter
// integration, rather than fake numbers.
const EsgSections = ({ monthly, social, governance }) => {
  const hasMonthly = monthly && monthly.length > 0;
  const hasSocial = social && social.length > 0;
  const hasGovernance = governance && governance.length > 0;

  return (
    <Tabs defaultValue="environment">
      <TabsList>
        <TabsTrigger value="environment">Environment</TabsTrigger>
        <TabsTrigger value="social">Social</TabsTrigger>
        <TabsTrigger value="governance">Governance</TabsTrigger>
        <TabsTrigger value="power-quality">Power Quality</TabsTrigger>
      </TabsList>

      <TabsContent value="environment" className="pt-5">
        {!hasMonthly ? (
          <p className="text-sm text-muted-foreground">No monthly data yet.</p>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  Electricity Consumption (kVAh)
                </p>
                <EsgMonthlyChart data={monthly} dataKey="electricity_kvah" unit="kVAh" color={ELECTRICITY_COLOR} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  GHG Emissions (tCO2e)
                </p>
                <EsgMonthlyChart data={monthly} dataKey="ghg_total_tco2e" unit="t CO2e" color={GHG_COLOR} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  Water Consumption (kL)
                </p>
                <EsgMonthlyChart data={monthly} dataKey="water_withdrawal_kl" unit="kL" color={WATER_COLOR} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  Diesel Consumption (Litres)
                </p>
                <EsgMonthlyChart data={monthly} dataKey="diesel_litres" unit="L" color={ELECTRICITY_COLOR} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  GHG Scope 1 (tCO2e)
                </p>
                <EsgMonthlyChart data={monthly} dataKey="ghg_scope1_tco2e" unit="t CO2e" color={ELECTRICITY_COLOR} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  GHG Scope 2 (tCO2e)
                </p>
                <EsgMonthlyChart data={monthly} dataKey="ghg_scope2_tco2e" unit="t CO2e" color={GHG_COLOR} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  GHG Scope 3 (tCO2e)
                </p>
                <EsgMonthlyChart data={monthly} dataKey="ghg_scope3_tco2e" unit="t CO2e" color={ELECTRICITY_COLOR} />
              </div>
            </div>
            <MonthlyTable monthly={monthly} />
          </>
        )}
      </TabsContent>

      <TabsContent value="social" className="pt-5">
        {hasSocial ? <MetricTable rows={social} /> : <NotTrackedYet label="Social" />}
      </TabsContent>

      <TabsContent value="governance" className="pt-5">
        {hasGovernance ? <MetricTable rows={governance} /> : <NotTrackedYet label="Governance" />}
      </TabsContent>

      <TabsContent value="power-quality" className="pt-5">
        <IntegrationPending />
      </TabsContent>
    </Tabs>
  );
};

export default EsgSections;
